import type {
  GetClubPasswordResponse,
  HGFounderClubPasswordReply
} from '@ps/web-shared-api-types/v0/SwHomeGames';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { handleServerResponse } from '@swf/poker';

import { ENDPOINTS } from '../../../constants/endpoints';
import useCheckUserClubRole from '../../../hooks/useCheckUserClubRole';
import { ModalRoutes, UserClubRole } from '../../../types/common';

import { TXTWCL_Something_went_wrongX_please_t_ELL } from './useGetClubPassword.messages.json';

type ClubPasswords = Record<string, string>;

const useGetClubPassword = (clubId: number) => {
  const request = useRequest();
  const { modal } = useDispatch<Dispatch>();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);
  const [clubPasswords, setClubPassword] = useState<ClubPasswords>({});
  const userHasRights = useCheckUserClubRole(`${clubId}`, [
    UserClubRole.Manager,
    UserClubRole.Admin
  ]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (userHasRights && !loading && !clubPasswords[`club-${clubId}`]) {
      try {
        setLoading(true);
        request({
          service: ENDPOINTS.clubPassword,
          http: true,
          body: { clubId },
          method: 'GET'
        }).subscribe(({ body }) => {
          const {
            error,
            data: { password }
          } = handleServerResponse<GetClubPasswordResponse, HGFounderClubPasswordReply>({
            response: body,
            accessor: 'clubPasswordReply'
          });
          if (!error && password) {
            setClubPassword((current) => ({
              ...current,
              [`club-${clubId}`]: password
            }));

            timeout = setTimeout(() => {
              setClubPassword((current) => {
                const passwords = { ...current };
                delete passwords[`club-${clubId}`];
                return passwords;
              });
            }, 1000 * 60 * 10);
          }

          setLoading(false);
        });
      } catch {
        const errorMsg = formatMessage(TXTWCL_Something_went_wrongX_please_t_ELL);
        modal.enqueue({
          path: ModalRoutes.notification,
          locationState: { text: errorMsg }
        });
      } finally {
        setLoading(false);
      }
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  return { loading, clubPassword: clubPasswords[`club-${clubId}`] };
};

export default useGetClubPassword;
